<script>
import Action from './Action.vue';
import Condition from './Condition.vue';
import Branch from './Branch.vue';
import ForEach from './ForEach.vue';
import { insertStepAt } from '../../../helpers/automationUtils';

export default {
  name: 'Step',
  components: {
    Action,
    Condition,
    Branch,
    ForEach,
  },
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    trigger: {
      type: Object,
      default: () => {},
    },
    first: {
      type: Boolean,
      default: false,
    },
    last: {
      type: Boolean,
      default: false,
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'stepChange',
    'stepDelete',
    'select',
    'addStepBefore',
    'addStepAfter',
  ],
  computed: {
    isConditionStep() {
      return this.step.action === 'branch';
    },
    isForEachStep() {
      return this.step.action === 'for_each';
    },
    branches() {
      return this.step.branches || [];
    },
    defaultBranch() {
      return this.step.default || {};
    },
    forEachSteps() {
      return this.step.steps || [];
    },
  },
  methods: {
    onStepChange(...step) {
      this.$emit('stepChange', ...step);
    },
    onStepDelete(...step) {
      this.$emit('stepDelete', ...step);
    },
    onBranchChange(index, data) {
      const step = this.step;
      if (index === null) {
        step.default = {
          ...step.default,
          ...data,
        };
      } else {
        step.branches = step.branches.map((branch, _index) => {
          if (index !== _index) return data;
          return {
            ...branch,
            ...data,
          };
        });
      }
      this.onStepChange(this.step.id, step);
    },
    addBranchStepAt(branchIndex, stepIndex, newStep) {
      const branchBefore =
        branchIndex === null ? this.defaultBranch : this.branches[branchIndex];
      const branch = {
        ...branchBefore,
        steps: insertStepAt(branchBefore.steps, stepIndex, newStep),
      };
      this.onStepChange(branch.id, branch);
    },
    deleteBranchStepAt(branchIndex, stepIndex) {
      const branchBefore =
        branchIndex === null ? this.defaultBranch : this.branches[branchIndex];
      const steps = branchBefore.steps.filter(
        (_, index) => stepIndex !== index
      );
      const branch = {
        ...branchBefore,
        steps,
      };
      this.onBranchChange(branchIndex, branch);
    },
    addForEachStepAt(stepIndex, newStep) {
      this.onStepChange(this.step.id, {
        steps: insertStepAt(this.forEachSteps, stepIndex, newStep),
      });
    },
  },
};
</script>

<template>
  <Condition
    v-if="isConditionStep"
    :step="step"
    :trigger="trigger"
    :first="first"
    :last="last"
    :variables="variables"
    :disabled="disabled"
    @select="e => $emit('select', e)"
    @add-step-before="e => $emit('addStepBefore', e)"
    @step-change="onStepChange"
    @step-delete="onStepDelete"
  >
    <template #branch>
      <Branch
        v-for="(branch, branchIndex) of branches"
        :key="branch.id"
        :branch="branch"
        :index="branchIndex"
        :disabled="disabled"
        :variables="variables"
        @select="e => $emit('select', e)"
        @step-change="onStepChange"
        @step-delete="onStepDelete"
      >
        <template #step="{ stepVariablesMap }">
          <step
            v-for="(branchStep, stepIndex) of branch.steps"
            :key="branchStep.id"
            :step="branchStep"
            :trigger="trigger"
            :last="stepIndex === branch.steps.length - 1"
            :variables="stepVariablesMap.get(branchStep.id) || []"
            :disabled="disabled"
            @add-step-before="e => addBranchStepAt(branchIndex, stepIndex, e)"
            @add-step-after="
              e => addBranchStepAt(branchIndex, stepIndex + 1, e)
            "
            @step-change="onStepChange"
            @step-delete="onStepDelete"
          />
        </template>
      </Branch>
      <Branch
        :branch="defaultBranch"
        :disabled="disabled"
        :variables="variables"
        @select="e => $emit('select', e)"
        @step-change="onStepChange"
        @step-delete="onStepDelete"
      >
        <template #step="{ stepVariablesMap }">
          <step
            v-for="(branchStep, stepIndex) of defaultBranch.steps"
            :key="branchStep.id"
            :step="branchStep"
            :trigger="trigger"
            :last="stepIndex === defaultBranch.steps.length - 1"
            :variables="stepVariablesMap.get(branchStep.id) || []"
            :disabled="disabled"
            @add-step-before="e => addBranchStepAt(null, stepIndex, e)"
            @add-step-after="e => addBranchStepAt(null, stepIndex + 1, e)"
            @step-change="onStepChange"
            @step-delete="onStepDelete"
          />
        </template>
      </Branch>
    </template>
  </Condition>

  <ForEach
    v-else-if="isForEachStep"
    :step="step"
    :trigger="trigger"
    :first="first"
    :last="last"
    :variables="variables"
    :disabled="disabled"
    @add-step-before="e => $emit('addStepBefore', e)"
    @add-step-after="e => $emit('addStepAfter', e)"
    @step-change="onStepChange"
    @step-delete="onStepDelete"
  >
    <template #step="{ stepVariablesMap }">
      <step
        v-for="(forEachStep, stepIndex) of forEachSteps"
        :key="forEachStep.id"
        :step="forEachStep"
        :trigger="trigger"
        :last="stepIndex === forEachSteps.length - 1"
        :variables="stepVariablesMap.get(forEachStep.id) || []"
        :disabled="disabled"
        @add-step-before="e => addForEachStepAt(stepIndex, e)"
        @add-step-after="e => addForEachStepAt(stepIndex + 1, e)"
        @step-change="onStepChange"
        @step-delete="onStepDelete"
      />
    </template>
  </ForEach>

  <Action
    v-else
    :step="step"
    :trigger="trigger"
    :first="first"
    :last="last"
    :variables="variables"
    :disabled="disabled"
    @select="e => $emit('select', e)"
    @add-step-before="e => $emit('addStepBefore', e)"
    @add-step-after="e => $emit('addStepAfter', e)"
    @step-delete="onStepDelete"
    @step-change="onStepChange"
  />
</template>
